import { loadImageBitmapFromDataUrl } from "./loadImageWithOrientation";
import type { CornerPoints } from "../../types/jscanify";
import ortWasmMjsUrl from "onnxruntime-web/ort-wasm-simd-threaded.mjs?url";
import ortWasmUrl from "onnxruntime-web/ort-wasm-simd-threaded.wasm?url";

const MODEL_PATH = "/models/docaligner/lcnet100_h_e_bifpn_256_fp32.onnx";
const MODEL_RESOLUTION = 256;
const HEATMAP_THRESHOLD = 0.3;
const MIN_COMPONENT_PIXELS = 3;
const INPUT_NAME = "img";

type Ort = typeof import("onnxruntime-web/wasm");
type OrtSession = Awaited<ReturnType<Ort["InferenceSession"]["create"]>>;

interface DocAlignerRuntime {
  ort: Ort;
  session: OrtSession;
}

interface PreprocessedImage {
  data: Float32Array;
  width: number;
  height: number;
}

type HeatmapLayout = "nchw" | "nhwc";

let runtimePromise: Promise<DocAlignerRuntime> | null = null;

const loadRuntime = async (): Promise<DocAlignerRuntime> => {
  if (!runtimePromise) {
    runtimePromise = import("onnxruntime-web/wasm")
      .then(async (ort) => {
        ort.env.wasm.wasmPaths = {
          mjs: ortWasmMjsUrl,
          wasm: ortWasmUrl,
        };
        ort.env.wasm.numThreads = 1;
        ort.env.wasm.simd = true;
        ort.env.wasm.proxy = true;

        const session = await ort.InferenceSession.create(MODEL_PATH, {
          executionProviders: ["wasm"],
          graphOptimizationLevel: "all",
          executionMode: "sequential",
        });

        return { ort, session };
      })
      .catch((error) => {
        runtimePromise = null;
        throw error;
      });
  }

  return runtimePromise;
};

const preprocessImage = (imageBitmap: ImageBitmap): PreprocessedImage => {
  const canvas = document.createElement("canvas");
  canvas.width = MODEL_RESOLUTION;
  canvas.height = MODEL_RESOLUTION;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("No se pudo crear el contexto del canvas para DocAligner");
  }

  ctx.drawImage(imageBitmap, 0, 0, MODEL_RESOLUTION, MODEL_RESOLUTION);
  const imageData = ctx.getImageData(0, 0, MODEL_RESOLUTION, MODEL_RESOLUTION);
  const size = MODEL_RESOLUTION * MODEL_RESOLUTION;
  const data = new Float32Array(3 * size);
  const inv255 = 1 / 255;

  for (let i = 0; i < size; i++) {
    const srcIdx = i * 4;
    data[i] = imageData.data[srcIdx + 2] * inv255;
    data[i + size] = imageData.data[srcIdx + 1] * inv255;
    data[i + size * 2] = imageData.data[srcIdx] * inv255;
  }

  canvas.width = 0;
  canvas.height = 0;

  return {
    data,
    width: imageBitmap.width,
    height: imageBitmap.height,
  };
};

const getHeatmapValue = (
  outputData: Float32Array,
  layout: HeatmapLayout,
  pointIndex: number,
  index: number,
  heatmapSize: number,
): number => {
  if (layout === "nchw") {
    return outputData[pointIndex * heatmapSize + index];
  }

  return outputData[index * 4 + pointIndex];
};

const findLargestHeatmapComponent = (
  outputData: Float32Array,
  layout: HeatmapLayout,
  pointIndex: number,
  heatmapWidth: number,
  heatmapHeight: number,
): { x: number; y: number } | null => {
  const heatmapSize = heatmapWidth * heatmapHeight;
  const visited = new Uint8Array(heatmapSize);
  const queue = new Int32Array(heatmapSize);
  let bestCount = 0;
  let bestScore = 0;
  let bestX = 0;
  let bestY = 0;
  let maxValue = -Infinity;

  for (let i = 0; i < heatmapSize; i++) {
    const value = getHeatmapValue(
      outputData,
      layout,
      pointIndex,
      i,
      heatmapSize,
    );
    if (value > maxValue) {
      maxValue = value;
    }
  }

  if (maxValue < HEATMAP_THRESHOLD) {
    return null;
  }

  for (let start = 0; start < heatmapSize; start++) {
    if (visited[start]) continue;

    const startValue = getHeatmapValue(
      outputData,
      layout,
      pointIndex,
      start,
      heatmapSize,
    );

    if (startValue < HEATMAP_THRESHOLD) {
      visited[start] = 1;
      continue;
    }

    let head = 0;
    let tail = 0;
    let count = 0;
    let score = 0;
    let sumX = 0;
    let sumY = 0;

    visited[start] = 1;
    queue[tail++] = start;

    while (head < tail) {
      const current = queue[head++];
      const value = getHeatmapValue(
        outputData,
        layout,
        pointIndex,
        current,
        heatmapSize,
      );
      const x = current % heatmapWidth;
      const y = Math.floor(current / heatmapWidth);

      count++;
      score += value;
      sumX += x;
      sumY += y;

      const neighbors = [
        current - 1,
        current + 1,
        current - heatmapWidth,
        current + heatmapWidth,
      ];

      for (const next of neighbors) {
        if (next < 0 || next >= heatmapSize || visited[next]) continue;
        if (next === current - 1 && x === 0) continue;
        if (next === current + 1 && x === heatmapWidth - 1) continue;

        const nextValue = getHeatmapValue(
          outputData,
          layout,
          pointIndex,
          next,
          heatmapSize,
        );

        if (nextValue < HEATMAP_THRESHOLD) {
          visited[next] = 1;
          continue;
        }

        visited[next] = 1;
        queue[tail++] = next;
      }
    }

    if (count > bestCount || (count === bestCount && score > bestScore)) {
      bestCount = count;
      bestScore = score;
      bestX = sumX / count;
      bestY = sumY / count;
    }
  }

  if (bestCount < MIN_COMPONENT_PIXELS) {
    return null;
  }

  return {
    x: bestX + 0.5,
    y: bestY + 0.5,
  };
};

const heatmapToCorners = (
  outputData: Float32Array,
  outputShape: readonly number[],
  imageWidth: number,
  imageHeight: number,
): CornerPoints | null => {
  if (outputShape.length !== 4) return null;

  const [, d2, d3, d4] = outputShape;
  const layout: HeatmapLayout | null =
    d2 === 4 ? "nchw" : d4 === 4 ? "nhwc" : null;
  if (!layout) return null;

  const [heatmapHeight, heatmapWidth] = layout === "nchw" ? [d3, d4] : [d2, d3];
  const heatmapSize = heatmapHeight * heatmapWidth;

  if (heatmapSize <= 0 || outputData.length < heatmapSize * 4) {
    return null;
  }

  const scaleX = imageWidth / heatmapWidth;
  const scaleY = imageHeight / heatmapHeight;
  const points: Array<{ x: number; y: number }> = [];

  for (let pointIndex = 0; pointIndex < 4; pointIndex++) {
    const point = findLargestHeatmapComponent(
      outputData,
      layout,
      pointIndex,
      heatmapWidth,
      heatmapHeight,
    );

    if (!point) return null;

    points.push({
      x: Math.max(0, Math.min(point.x * scaleX, imageWidth)),
      y: Math.max(0, Math.min(point.y * scaleY, imageHeight)),
    });
  }

  const corners = {
    topLeftCorner: points[0],
    topRightCorner: points[1],
    bottomRightCorner: points[2],
    bottomLeftCorner: points[3],
  };

  return hasValidGeometry(corners, imageWidth, imageHeight) ? corners : null;
};

const hasValidGeometry = (
  corners: CornerPoints,
  imageWidth: number,
  imageHeight: number,
): boolean => {
  const points = [
    corners.topLeftCorner,
    corners.topRightCorner,
    corners.bottomRightCorner,
    corners.bottomLeftCorner,
  ];

  const allInBounds = points.every(
    (point) =>
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      point.x >= 0 &&
      point.x <= imageWidth &&
      point.y >= 0 &&
      point.y <= imageHeight,
  );

  if (!allInBounds) return false;

  const topAboveBottom =
    corners.topLeftCorner.y < corners.bottomLeftCorner.y &&
    corners.topRightCorner.y < corners.bottomRightCorner.y;
  const leftBeforeRight =
    corners.topLeftCorner.x < corners.topRightCorner.x &&
    corners.bottomLeftCorner.x < corners.bottomRightCorner.x;

  if (!topAboveBottom || !leftBeforeRight) return false;

  let signedArea = 0;
  for (let i = 0; i < points.length; i++) {
    const next = points[(i + 1) % points.length];
    signedArea += points[i].x * next.y - next.x * points[i].y;
  }

  const minArea = imageWidth * imageHeight * 0.02;
  return signedArea > minArea;
};

export const detectDocumentCornersWithDocAligner = async (
  imageSrc: string,
): Promise<CornerPoints | null> => {
  let imageBitmap: ImageBitmap | null = null;

  try {
    const { ort, session } = await loadRuntime();
    imageBitmap = await loadImageBitmapFromDataUrl(imageSrc);
    const preprocessed = preprocessImage(imageBitmap);

    const input = new ort.Tensor("float32", preprocessed.data, [
      1,
      3,
      MODEL_RESOLUTION,
      MODEL_RESOLUTION,
    ]);

    const result = await session.run({ [INPUT_NAME]: input });
    const output = result.heatmap ?? result[session.outputNames[0]];

    if (!output || output.type !== "float32") {
      return null;
    }

    return heatmapToCorners(
      output.data as Float32Array,
      output.dims,
      preprocessed.width,
      preprocessed.height,
    );
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("DocAligner corner detection failed:", error);
    }
    return null;
  } finally {
    imageBitmap?.close();
  }
};
